import type { ExpenseFormSubmit } from "../components/ExpenseForm";

type ExpenseLikePayments = ExpenseFormSubmit["payments"];
type ExpenseLikeItems = ExpenseFormSubmit["items"];

/**
 * Structural shape used for split-equality. Both sides reduce to
 * `{ type, divisions: [{participantId, ...maybeAmount, ...maybeValue}] }`,
 * so taking a permissive structural type keeps the helper usable from both
 * ExpenseFormSubmit (discriminated union) and the API-derived RecurringExpense
 * shape without a cast at every call site.
 */
type SplitLike = {
  type: string;
  divisions: ReadonlyArray<Record<string, unknown>>;
};

/**
 * Field-by-field equality for the edit-page diff helpers.
 *
 * Both pages (EditExpensePage and EditRecurringExpensePage) want to send
 * only the sections the user actually changed. Comparing entire JSON blobs
 * would be order-sensitive on `divisions`; comparing structurally lets us
 * skip a server-side re-validation when nothing meaningful changed.
 */
export function paymentsEqual(
  a: ExpenseLikePayments,
  b: ReadonlyArray<{
    participantId: string;
    amount: number;
    charge: number;
  }>,
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (
      x.participantId !== y.participantId ||
      x.amount !== y.amount ||
      x.charge !== y.charge
    ) {
      return false;
    }
  }
  return true;
}

export function splitsEqual(a: SplitLike, b: SplitLike): boolean {
  if (a.type !== b.type) return false;
  if (a.divisions.length !== b.divisions.length) return false;
  for (let i = 0; i < a.divisions.length; i++) {
    const x = a.divisions[i]!;
    const y = b.divisions[i]!;
    if (x.participantId !== y.participantId) return false;
    if ("amount" in x && x.amount !== y.amount) return false;
    if ("value" in x && x.value !== y.value) return false;
  }
  return true;
}

/**
 * Compare full item shapes (id + label + cost + split). Use
 * {@link itemsEqualByContent} when the right-hand side has no `id`.
 */
export function itemsEqual(
  a: ExpenseLikeItems,
  b: ReadonlyArray<{
    id?: string;
    label: string;
    cost: number;
    split: SplitLike;
  }>,
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (
      x.id !== y.id ||
      x.label !== y.label ||
      x.cost !== y.cost ||
      !splitsEqual(x.split, y.split)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Compare item shapes by content only (label/cost/split). Used by callers
 * whose persisted side has no `id` — recurring-expense templates store
 * items without per-item identity.
 */
export function itemsEqualByContent(
  a: ReadonlyArray<{ label: string; cost: number; split: SplitLike }>,
  b: ReadonlyArray<{ label: string; cost: number; split: SplitLike }>,
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (x.label !== y.label || x.cost !== y.cost) return false;
    if (!splitsEqual(x.split, y.split)) return false;
  }
  return true;
}
